import React from 'react';
import usePayments from '../../../hooks/api/usePayments';
import { getIntl } from '../../../utils/intl';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { ContentStack } from '../../v2BaseComponents/Stack';
import { Input } from '../../v2BaseComponents/Inputs';
import { PromptActions } from '../../v2BaseComponents/Prompt';

const intl = getIntl('RegistrationCodeInputField', {
    invalidErrorMessage: '"{code}" is not a valid code',
    submit: 'Apply Code',
    code: 'Code',
});

export type RegistrationCodeInputFieldOwnProps = {
    onSubmit: (code: string) => void;
    isApplyingRegistrationCode: boolean;
};

export type RegistrationCodeInputFieldProps = RegistrationCodeInputFieldOwnProps;

const RegistrationCodeInputField: React.FC<RegistrationCodeInputFieldProps> = ({
    onSubmit,
    isApplyingRegistrationCode,
}) => {
    const [inputCode, setInputCode] = React.useState('');
    const debouncedValue = useDebouncedValue<string>({
        debouncedValue: inputCode,
        debouncedOnChange: setInputCode,
        debounceInterval: 200,
    });
    const { isValidatingRegistrationCode, registrationCodeValidationResult } = usePayments({
        registrationCodeValidationInput: inputCode,
    });

    return (
        <ContentStack>
            <Input
                testID="registration-code-input"
                value={debouncedValue.value}
                onChange={(e) => {
                    debouncedValue.onChange(e.nativeEvent.text.toLocaleUpperCase());
                }}
                error={
                    registrationCodeValidationResult?.valid || !inputCode
                        ? undefined
                        : intl('invalidErrorMessage', { code: inputCode })
                }
                title={intl('code')}
            />
            <PromptActions
                actions={[
                    {
                        title: intl('submit'),
                        onPress: () => onSubmit(inputCode),
                        isDisabled:
                            !inputCode ||
                            !registrationCodeValidationResult?.valid ||
                            isApplyingRegistrationCode ||
                            isValidatingRegistrationCode,
                        icon: 'Check',
                        testID: 'registration-code-submit-button',
                    },
                ]}
            />
        </ContentStack>
    );
};

export default React.memo(RegistrationCodeInputField);
